import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Lock,
  Loader2,
  LogOut,
  ShieldAlert,
  Send,
  Instagram,
  Music2,
  CheckCircle2,
  History,
  Package as PackageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PW_KEY = "boostup_reseller_pw";
const fmt = (n) => n.toLocaleString();
const RATE_PER_K = { instagram: 3, tiktok: 10 };

const ResellerLogin = ({ onUnlock }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reseller/login", { password: pw });
      localStorage.setItem(PW_KEY, pw);
      onUnlock(pw);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 py-24">
        <form
          onSubmit={submit}
          data-testid="reseller-login-form"
          className="w-full max-w-sm bg-[#0A0A0A] border border-white/10 rounded-2xl p-8"
        >
          <div className="w-12 h-12 rounded-xl bg-[#FF007F]/10 grid place-items-center mb-6">
            <Lock className="w-5 h-5 text-[#FF007F]" />
          </div>
          <h1 className="font-display text-2xl font-black tracking-tighter mb-2">
            Reseller panel
          </h1>
          <p className="text-sm text-white/50 mb-6">
            Enter the reseller password to submit orders directly.
          </p>
          <Input
            data-testid="reseller-password-input"
            type="password"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Password"
            className="bg-transparent border-white/10 text-white focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] mb-4"
          />
          <button
            type="submit"
            data-testid="reseller-login-submit"
            disabled={busy || !pw}
            className="w-full bg-neon text-white font-bold py-3 rounded-full hover:scale-[1.02] transition-transform glow-pink disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Unlock
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
};

const statusChip = (s) => {
  if (s === "delivering") return "bg-[#FF5E00]/10 text-[#FF5E00] border-[#FF5E00]/30";
  if (s === "delivered") return "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/30";
  if (s === "failed") return "bg-red-500/10 text-red-400 border-red-500/30";
  if (s === "pending" || s === "processing") return "bg-white/5 text-white/60 border-white/10";
  return "bg-white/5 text-white/60 border-white/10";
};

const summarize = (orders) => {
  // Consider only paid/dispatched orders (not "pending" unpaid Stripe intents)
  const eligible = orders.filter(
    (o) => o.payment_status === "paid" || o.payment_status === "reseller"
  );
  const now = new Date();
  const monthOrders = eligible.filter((o) => {
    const d = new Date(o.created_at);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  });
  const sum = (arr, key) => arr.reduce((a, b) => a + (b[key] || 0), 0);
  return {
    total_orders: eligible.length,
    total_followers: sum(eligible, "followers"),
    total_revenue: sum(eligible, "amount"),
    month_orders: monthOrders.length,
    month_followers: sum(monthOrders, "followers"),
    month_revenue: sum(monthOrders, "amount"),
  };
};

const EarningsCard = ({ orders }) => {
  const s = summarize(orders);
  const monthName = new Date().toLocaleString(undefined, { month: "long" });
  return (
    <div
      data-testid="reseller-earnings-card"
      className="mt-10 bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 sm:p-8"
    >
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-6">
        <h2 className="font-display text-xl font-black tracking-tighter">
          Earnings <span className="text-gradient-neon">{monthName}</span>
        </h2>
        <span className="text-white/40 text-xs uppercase tracking-[0.2em] font-bold">
          Lifetime totals below
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
        <Stat
          label={`${monthName} revenue`}
          value={`$${s.month_revenue.toFixed(2)}`}
          testid="stat-month-revenue"
          highlight
        />
        <Stat
          label={`${monthName} orders`}
          value={s.month_orders.toLocaleString()}
          testid="stat-month-orders"
        />
        <Stat
          label={`${monthName} followers sold`}
          value={s.month_followers.toLocaleString()}
          testid="stat-month-followers"
        />
        <Stat
          label="Lifetime revenue"
          value={`$${s.total_revenue.toFixed(2)}`}
          testid="stat-total-revenue"
          muted
        />
        <Stat
          label="Lifetime orders"
          value={s.total_orders.toLocaleString()}
          testid="stat-total-orders"
          muted
        />
        <Stat
          label="Lifetime followers"
          value={s.total_followers.toLocaleString()}
          testid="stat-total-followers"
          muted
        />
      </div>
    </div>
  );
};

const Stat = ({ label, value, testid, highlight, muted }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/40 mb-2">
      {label}
    </div>
    <div
      data-testid={testid}
      className={`font-display font-black tracking-tighter ${
        highlight
          ? "text-4xl text-gradient-neon"
          : muted
          ? "text-2xl text-white/70"
          : "text-3xl text-white"
      }`}
    >
      {value}
    </div>
  </div>
);

const OrderHistory = ({ orders, loading }) => {
  return (
    <div className="mt-10">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-4 h-4 text-white/60" />
        <h2 className="font-display text-xl font-black tracking-tighter">
          Order history
        </h2>
        <span className="text-white/40 text-xs">({orders.length})</span>
      </div>

      {loading && (
        <div className="text-center py-10 text-white/40">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading…
        </div>
      )}

      {!loading && orders.length === 0 && (
        <div
          data-testid="reseller-history-empty"
          className="text-center py-14 border border-dashed border-white/10 rounded-2xl text-white/40"
        >
          <PackageIcon className="w-6 h-6 mx-auto mb-3 opacity-40" />
          <div className="text-sm">No reseller orders yet.</div>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div
          data-testid="reseller-history-list"
          className="bg-[#0A0A0A] border border-white/10 rounded-2xl divide-y divide-white/5 overflow-hidden"
        >
          {orders.map((o) => {
            const isTk = (o.platform || "instagram") === "tiktok";
            return (
              <div
                key={o.id}
                data-testid={`reseller-history-row-${o.id}`}
                className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 px-5 py-4 hover:bg-white/[0.02] transition"
              >
                <div className="flex items-center gap-2 md:min-w-[110px]">
                  <span
                    className={`text-[10px] uppercase font-bold tracking-[0.15em] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${
                      isTk
                        ? "bg-[#00F2EA]/10 text-[#00F2EA] border-[#00F2EA]/30"
                        : "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/30"
                    }`}
                  >
                    {isTk ? <Music2 className="w-3 h-3" /> : <Instagram className="w-3 h-3" />}
                    {isTk ? "TikTok" : "Instagram"}
                  </span>
                </div>
                <div className="font-mono text-white flex-1 min-w-[140px] truncate">
                  @{o.instagram_username}
                </div>
                <div className="text-white/70 text-sm whitespace-nowrap">
                  {fmt(o.followers)} followers
                </div>
                <div className="text-gradient-neon font-bold whitespace-nowrap w-16 text-right">
                  ${(o.amount || 0).toFixed(2)}
                </div>
                <div className="whitespace-nowrap">
                  <span
                    className={`text-[10px] uppercase font-bold tracking-[0.15em] px-2 py-1 rounded-full border ${statusChip(
                      o.status
                    )}`}
                  >
                    {o.status}
                  </span>
                </div>
                <div className="text-white/40 text-xs whitespace-nowrap md:min-w-[110px] md:text-right">
                  {o.panel_order_id ? `#${o.panel_order_id}` : "—"}
                </div>
                <div className="text-white/40 text-xs whitespace-nowrap md:min-w-[130px] md:text-right">
                  {new Date(o.created_at).toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Reseller() {
  const [pw, setPw] = useState(() => localStorage.getItem(PW_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [, setChecking] = useState(!!pw);

  const [platform, setPlatform] = useState("instagram");
  const [link, setLink] = useState("");
  const [followers, setFollowers] = useState(1000);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const isTikTok = platform === "tiktok";
  const rate = RATE_PER_K[platform];
  const price = +((followers * rate) / 1000).toFixed(2);

  // Auto-verify existing password on mount
  useEffect(() => {
    if (!pw) {
      setChecking(false);
      return;
    }
    api
      .post("/reseller/login", { password: pw })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(PW_KEY);
        setPw("");
      })
      .finally(() => setChecking(false));
  }, []);

  const fetchHistory = async (pwToUse) => {
    const password = pwToUse || pw;
    if (!password) return;
    setHistoryLoading(true);
    try {
      const r = await api.get("/reseller/orders", {
        headers: { "X-Reseller-Password": password },
      });
      setHistory(r.data || []);
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error("Failed to load history");
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (authed) fetchHistory();
  }, [authed]);

  const logout = () => {
    localStorage.removeItem(PW_KEY);
    setPw("");
    setAuthed(false);
    setLink("");
    setFollowers(1000);
    setLastResult(null);
    setHistory([]);
  };

  const openConfirm = () => {
    if (!link.trim()) {
      toast.error(
        `Paste a ${isTikTok ? "TikTok" : "Instagram"} profile link or username`
      );
      return;
    }
    setConfirmOpen(true);
  };

  const submitOrder = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    setLastResult(null);
    try {
      const r = await api.post(
        "/reseller/order",
        { link: link.trim(), followers, platform },
        { headers: { "X-Reseller-Password": pw } }
      );
      // Reseller now pays via Stripe — redirect to Checkout
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      // Fallback (shouldn't happen)
      toast.success("Order created");
      fetchHistory();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired");
        logout();
        return;
      }
      setLastResult({
        ok: false,
        error: e?.response?.data?.detail || "Failed to create order",
      });
      toast.error(e?.response?.data?.detail || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  if (!authed) {
    return (
      <ResellerLogin
        onUnlock={(p) => {
          setPw(p);
          setAuthed(true);
          setChecking(false);
        }}
      />
    );
  }

  const MIN = 500;
  const MAX = 50000;
  const platformLabel = isTikTok ? "TikTok" : "Instagram";
  const platformIcon = isTikTok ? Music2 : Instagram;
  const PlatformIcon = platformIcon;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="uppercase text-xs font-bold tracking-[0.2em] text-[#FF007F] mb-3">
              Reseller
            </div>
            <h1 className="font-display text-4xl font-black tracking-tighter">
              Instant <span className="text-gradient-neon">order</span>
            </h1>
            <p className="text-white/60 mt-2 text-sm">
              Paste a profile link, pick the follower count, pay via Stripe. Order auto-dispatches to the panel once paid.
            </p>
          </div>
          <button
            data-testid="reseller-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/70 px-4 py-2.5 rounded-full hover:bg-white/10 hover:text-white transition text-sm self-start md:self-auto"
          >
            <LogOut className="w-4 h-4" />
            Lock
          </button>
        </div>

        {/* Platform toggle */}
        <div className="mb-6 flex justify-center">
          <div
            data-testid="reseller-platform-toggle"
            className="inline-flex items-center gap-1 p-1.5 rounded-full border border-white/10 bg-[#0A0A0A]"
          >
            <button
              data-testid="reseller-platform-instagram"
              onClick={() => setPlatform("instagram")}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all ${
                !isTikTok
                  ? "bg-neon text-white glow-pink"
                  : "text-white/60 hover:text-white"
              }`}
            >
              <Instagram className="w-4 h-4" />
              Instagram · $3/1K
            </button>
            <button
              data-testid="reseller-platform-tiktok"
              onClick={() => setPlatform("tiktok")}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all ${
                isTikTok
                  ? "bg-gradient-tiktok text-white glow-tiktok"
                  : "text-white/60 hover:text-white"
              }`}
            >
              <Music2 className="w-4 h-4" />
              TikTok · $10/1K
            </button>
          </div>
        </div>

        {/* Warning banner */}
        <div
          data-testid="reseller-warning-banner"
          className="mb-8 border border-[#FF5E00]/40 bg-[#FF5E00]/5 rounded-2xl p-5 flex items-start gap-3"
        >
          <ShieldAlert className="w-5 h-5 text-[#FF5E00] shrink-0 mt-0.5" />
          <div className="text-sm text-white/80 leading-relaxed">
            <div className="font-bold text-[#FF5E00] mb-1">Read before submitting</div>
            The {platformLabel} account <span className="font-bold text-white">must be public</span>{" "}
            and the link/username must be correct.{" "}
            <span className="font-bold text-white">
              No refunds if you submit a private, wrong, or non-existent account.
            </span>
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8">
          {/* Profile link input */}
          <div className="mb-6">
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2 flex items-center gap-2">
              <PlatformIcon className="w-3.5 h-3.5" /> {platformLabel} profile link or username
            </label>
            <Input
              data-testid="reseller-link-input"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder={
                isTikTok
                  ? "https://www.tiktok.com/@yourhandle  or  @yourhandle"
                  : "https://www.instagram.com/yourhandle  or  @yourhandle"
              }
              className={`bg-transparent border-white/10 text-white focus:ring-1 ${
                isTikTok
                  ? "focus:border-[#00F2EA] focus:ring-[#00F2EA]"
                  : "focus:border-[#FF007F] focus:ring-[#FF007F]"
              }`}
            />
          </div>

          {/* Follower slider */}
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-3 gap-4">
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/60">
                Followers to send
              </label>
              <div className="flex items-baseline gap-3">
                <div
                  data-testid="reseller-followers-display"
                  className="font-display text-3xl font-black tracking-tighter text-white"
                >
                  {fmt(followers)}
                </div>
                <div className="text-white/30 font-black text-lg">·</div>
                <div
                  data-testid="reseller-price-display"
                  className={`font-display text-3xl font-black tracking-tighter ${
                    isTikTok ? "text-gradient-tiktok" : "text-gradient-neon"
                  }`}
                >
                  ${price.toFixed(2)}
                </div>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/40 mb-3 text-right">
              Reseller rate · ${rate} per 1K {platformLabel}
            </div>
            <Slider
              data-testid="reseller-followers-slider"
              value={[followers]}
              onValueChange={([v]) => setFollowers(v)}
              min={MIN}
              max={MAX}
              step={100}
              className={
                isTikTok
                  ? "[&_[role=slider]]:bg-gradient-tiktok [&_[role=slider]]:border-0 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:shadow-[0_0_24px_rgba(0,242,234,0.6)]"
                  : "[&_[role=slider]]:bg-neon [&_[role=slider]]:border-0 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:shadow-[0_0_24px_rgba(255,0,127,0.6)]"
              }
            />
            <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] font-bold text-white/40 mt-2">
              <span>500</span>
              <span>10K</span>
              <span>25K</span>
              <span>50K</span>
            </div>

            {/* Quick picks */}
            <div className="flex flex-wrap gap-2 mt-4">
              {[1000, 2500, 5000, 10000, 25000, 50000].map((v) => (
                <button
                  key={v}
                  onClick={() => setFollowers(v)}
                  data-testid={`reseller-quick-${v}`}
                  className={`text-xs font-bold px-3 py-1.5 rounded-full border transition ${
                    followers === v
                      ? isTikTok
                        ? "bg-[#00F2EA]/10 border-[#00F2EA]/40 text-[#00F2EA]"
                        : "bg-[#FF007F]/10 border-[#FF007F]/40 text-[#FF007F]"
                      : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                  }`}
                >
                  {fmt(v)}
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="reseller-submit-btn"
            onClick={openConfirm}
            disabled={submitting || !link.trim()}
            className={`w-full text-white font-bold py-4 rounded-full hover:scale-[1.01] transition-transform inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
              isTikTok ? "bg-gradient-tiktok glow-tiktok" : "bg-neon glow-pink"
            }`}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to checkout…
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Pay & submit · ${price.toFixed(2)}
              </>
            )}
          </button>
        </div>

        {/* Last result */}
        {lastResult?.ok && (
          <div
            data-testid="reseller-success-card"
            className={`mt-6 rounded-2xl p-6 border ${
              lastResult.platform === "tiktok"
                ? "border-[#00F2EA]/30 bg-[#00F2EA]/5"
                : "border-[#FF007F]/30 bg-[#FF007F]/5"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2
                className={`w-5 h-5 ${
                  lastResult.platform === "tiktok" ? "text-[#00F2EA]" : "text-[#FF007F]"
                }`}
              />
              <span className="font-bold text-white">Order sent to panel</span>
            </div>
            <div className="text-sm text-white/70">
              <span className="font-mono text-white">@{lastResult.handle}</span> — {fmt(lastResult.followers)} {lastResult.platform === "tiktok" ? "TikTok" : "Instagram"} followers · $
              {(lastResult.price || 0).toFixed(2)} · panel{" "}
              <span
                className={`font-mono ${
                  lastResult.platform === "tiktok" ? "text-[#00F2EA]" : "text-[#FF007F]"
                }`}
              >
                #{lastResult.panel_order_id}
              </span>
            </div>
          </div>
        )}

        {lastResult && !lastResult.ok && (
          <div className="mt-6 border border-red-500/30 bg-red-500/5 rounded-2xl p-6 text-sm text-red-300">
            <div className="font-bold mb-1">Order failed</div>
            {lastResult.error}
          </div>
        )}

        {/* Earnings + Order history */}
        <EarningsCard orders={history} />
        <OrderHistory orders={history} loading={historyLoading} />
      </main>
      <Footer />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-[#FF5E00]" />
              Before you submit
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70 leading-relaxed pt-2 space-y-2">
              <span className="block">
                You&apos;ll be sent to Stripe to pay{" "}
                <span
                  className={`font-bold ${
                    isTikTok ? "text-gradient-tiktok" : "text-gradient-neon"
                  }`}
                >
                  ${price.toFixed(2)}
                </span>{" "}
                for{" "}
                <span className="font-bold text-white">{fmt(followers)} {platformLabel} followers</span>{" "}
                to{" "}
                <span className="font-mono text-[#FF007F]">{link.trim() || "—"}</span>.
                After payment, the order dispatches to the panel automatically.
              </span>
              <span className="block">
                • The account <span className="font-bold text-white">must be public</span>.
              </span>
              <span className="block">
                • The link/username must be correct.
              </span>
              <span className="block text-[#FF5E00] font-semibold">
                No refunds if you mess this up.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reseller-confirm-submit"
              onClick={submitOrder}
              className={`text-white hover:opacity-90 ${
                isTikTok ? "bg-gradient-tiktok" : "bg-neon"
              }`}
            >
              I understand — pay ${price.toFixed(2)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
